import { useState, useEffect, useCallback } from "react";
import { api, CATEGORIES, formatApiError } from "@/lib/api";
import logo from "@/assets/logo.png";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import TaskCard from "@/components/TaskCard";
import TaskDialog from "@/components/TaskDialog";
import TaskDetail from "@/components/TaskDetail";
import AIAssistant from "@/components/AIAssistant";
import NotificationBell from "@/components/NotificationBell";
import TeamPanel from "@/components/TeamPanel";
import VoiceCapture from "@/components/VoiceCapture";
import RemindersWidget from "@/components/RemindersWidget";
import RecurringPanel from "@/components/RecurringPanel";
import RecurringDashboardWidget from "@/components/RecurringDashboardWidget";
import SettingsPanel from "@/components/SettingsPanel";
import AppGuide from "@/components/AppGuide";
import FirstTimeSetup from "@/components/FirstTimeSetup";
import MeetingsPanel from "@/components/MeetingsPanel";
import MeetingSummaryFlow from "@/components/MeetingSummaryFlow";
import EmployeesAccess from "@/components/EmployeesAccess";
import SchedulePanel from "@/components/SchedulePanel";
import TimeOffPanel from "@/components/TimeOffPanel";
import AvailabilityPanel from "@/components/AvailabilityPanel";
import CommNotesPanel from "@/components/CommNotesPanel";
import CommWidget from "@/components/CommWidget";
import CalendarPanel from "@/components/CalendarPanel";
import LayoutCustomizer from "@/components/LayoutCustomizer";
import {
  MagnifyingGlass, Plus, Sparkle, SignOut, Sun, Warning,
  ListChecks, CheckCircle, House, CalendarBlank, ArrowClockwise, Users, Microphone, ArrowsClockwise, GearSix, VideoCamera, ShieldCheck, CalendarX, CalendarCheck, List, X, ChatCircleText, Faders, CaretDown, CaretUp, BookOpen,
} from "@phosphor-icons/react";

// Order + hide items based on a saved layout ({ order:[keys], hidden:[keys] }). Stable for unknown keys.
const applyLayout = (items, layout) => {
  const order = layout?.order || [];
  const hidden = layout?.hidden || [];
  const idx = (k) => { const i = order.indexOf(k); return i === -1 ? 999 : i; };
  return items.filter((it) => !hidden.includes(it.key)).sort((a, b) => idx(a.key) - idx(b.key));
};

const STAT_META = [
  { key: "today", label: "Today", Icon: Sun, bg: "#E0F2FE" },
  { key: "overdue", label: "Overdue", Icon: Warning, bg: "#FEE2E2" },
  { key: "incomplete", label: "Incomplete", Icon: ListChecks, bg: "#FEF9C3" },
  { key: "completed", label: "Completed", Icon: CheckCircle, bg: "#DCFCE7" },
];

const TABS = [
  { key: "today", label: "Today" },
  { key: "overdue", label: "Overdue" },
  { key: "incomplete", label: "Incomplete" },
  { key: "completed", label: "Completed" },
  { key: "review", label: "Review", managerOnly: true },
  { key: "all", label: "All" },
];

const ROLE_SUBTITLE = {
  owner: "Company command center",
  store_manager: "Store operations",
  assistant_manager: "Store operations",
  shift_lead: "Your shift",
  sales_associate: "Your shift & tasks",
  stock_associate: "Stockroom & receiving",
  visual_merchandiser: "Merchandising & displays",
  read_only: "View-only access",
};

export default function Dashboard() {
  const { user, logout, prefs, loadPrefs, savePrefs, can } = useAuth();
  const isManager = ["owner", "store_manager", "assistant_manager"].includes(user.role);
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("today");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [allTasks, setAllTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [meetingsOpen, setMeetingsOpen] = useState(false);
  const [generateMeeting, setGenerateMeeting] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [timeoffOpen, setTimeoffOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [startupApplied, setStartupApplied] = useState(false);
  const [remindersKey, setRemindersKey] = useState(0);
  const [recurringKey, setRecurringKey] = useState(0);
  const [commOpen, setCommOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [allowCommNotes, setAllowCommNotes] = useState(false);
  const [commUnread, setCommUnread] = useState(0);
  const [commKey, setCommKey] = useState(0);
  const [customizerType, setCustomizerType] = useState(null); // null | "toolbar" | "dashboard"
  const [custMenuOpen, setCustMenuOpen] = useState(false);

  const voiceEnabled = prefs?.voice_feature_enabled !== false;
  const showFirstTime = prefs && !prefs.first_time_setup_complete;
  const canCreateTask = true;

  // Apply startup preference once, after setup is complete
  useEffect(() => {
    if (!prefs || startupApplied || showFirstTime) return;
    if (prefs.voice_feature_enabled !== false && prefs.voice_capture_on_startup) {
      setVoiceOpen(true);
    }
    setStartupApplied(true);
  }, [prefs, startupApplied, showFirstTime]);

  const openTaskById = async (id) => {
    try { const { data } = await api.get(`/tasks/${id}`); setDetail(data); } catch {}
  };

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  const loadDash = useCallback(async () => {
    const { data } = await api.get("/dashboard");
    setDash(data);
  }, []);

  const loadAll = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (category !== "all") params.category = category;
    const { data } = await api.get("/tasks", { params });
    setAllTasks(data);
  }, [search, category]);

  useEffect(() => { loadDash(); loadUsers(); }, [loadDash, loadUsers]);
  useEffect(() => { api.get("/settings/global").then((r) => setAllowCommNotes(!!r.data.allow_comm_notes)).catch(() => {}); }, []);
  const loadCommUnread = useCallback(() => { if (!allowCommNotes) return; api.get("/comm/unread-count").then((r) => setCommUnread(r.data.count || 0)).catch(() => {}); }, [allowCommNotes]);
  useEffect(() => { loadCommUnread(); const t = setInterval(loadCommUnread, 20000); return () => clearInterval(t); }, [loadCommUnread]);
  useEffect(() => { if (!commOpen) { loadCommUnread(); setCommKey((k) => k + 1); } }, [commOpen, loadCommUnread]);
  useEffect(() => { if (tab === "all") loadAll(); }, [tab, loadAll]);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
      else if (e.key === "Escape") setPaletteOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const refreshAll = () => { loadDash(); if (tab === "all") loadAll(); };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (t) => { setDetail(null); setEditing(t); setDialogOpen(true); };
  const onDetailChanged = (updated) => { setDetail(updated); refreshAll(); };

  const rollover = async () => {
    try { const { data } = await api.post("/tasks/rollover"); toast.success(`Moved ${data.moved} overdue tasks to tomorrow`); refreshAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const listForTab = () => {
    if (!dash) return [];
    if (tab === "all") return allTasks;
    if (tab === "review") return dash.to_review || [];
    return dash[tab] || [];
  };
  const tasks = listForTab();

  const closeAll = () => { setScheduleOpen(false); setMeetingsOpen(false); setRecurringOpen(false); setTimeoffOpen(false); setAvailabilityOpen(false); setTeamOpen(false); setAccessOpen(false); setAiOpen(false); setVoiceOpen(false); setSettingsOpen(false); setGuideOpen(false); setCommOpen(false); setCalendarOpen(false); setMobileMenuOpen(false); };
  const goHome = () => { closeAll(); setTab("today"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const nav = (fn) => { setMobileMenuOpen(false); fn(); };
  const voiceShow = voiceEnabled;

  const navGroups = [
    { group: "Overview", items: [
      { key: "dashboard", label: "Dashboard", Icon: House, show: true, onClick: goHome },
    ] },
    { group: "Operations", items: [
      { key: "schedule", label: "Schedule", Icon: CalendarBlank, show: can("schedule_view"), onClick: () => nav(() => setScheduleOpen(true)) },
      { key: "meetings", label: "Meetings", Icon: VideoCamera, show: can("meetings"), onClick: () => nav(() => setMeetingsOpen(true)) },
      { key: "recurring", label: "Recurring", Icon: ArrowsClockwise, show: isManager, onClick: () => nav(() => setRecurringOpen(true)) },
      { key: "calendar", label: "My Calendar", Icon: CalendarBlank, show: can("calendar"), onClick: () => nav(() => setCalendarOpen(true)) },
    ] },
    { group: "People", items: [
      { key: "timeoff", label: "Time Off", Icon: CalendarX, show: can("timeoff_request"), onClick: () => nav(() => setTimeoffOpen(true)) },
      { key: "availability", label: "Availability", Icon: CalendarCheck, show: can("availability_manage"), onClick: () => nav(() => setAvailabilityOpen(true)) },
      { key: "team", label: "Team", Icon: Users, show: can("employee_directory"), onClick: () => nav(() => setTeamOpen(true)) },
      { key: "access", label: "Access", Icon: ShieldCheck, show: can("user_management"), onClick: () => nav(() => setAccessOpen(true)) },
      { key: "comm", label: isManager ? "Team Messages" : "Message Management", Icon: ChatCircleText, show: allowCommNotes, onClick: () => nav(() => setCommOpen(true)), badge: commUnread },
    ] },
    { group: "Assist", items: [
      { key: "ai", label: "AI Assistant", Icon: Sparkle, show: can("ai_assistant"), onClick: () => nav(() => setAiOpen(true)) },
      { key: "voice", label: "Voice Capture", Icon: Microphone, show: voiceShow, onClick: () => nav(() => setVoiceOpen(true)) },
    ] },
    { group: "System", items: [
      { key: "guide", label: "App Guide", Icon: BookOpen, show: true, onClick: () => nav(() => setGuideOpen(true)) },
      { key: "settings", label: "Settings", Icon: GearSix, show: true, onClick: () => nav(() => setSettingsOpen(true)) },
    ] },
  ];
  const activeKey = scheduleOpen ? "schedule" : meetingsOpen ? "meetings" : recurringOpen ? "recurring" : timeoffOpen ? "timeoff" : availabilityOpen ? "availability" : teamOpen ? "team" : accessOpen ? "access" : aiOpen ? "ai" : voiceOpen ? "voice" : settingsOpen ? "settings" : "dashboard";
  const availableNav = navGroups.flatMap((g) => g.items).filter((i) => i.show);

  // Customizable top toolbar icons
  const toolbarItems = [
    { key: "voice", title: "Voice", show: voiceShow && prefs?.show_floating_microphone !== false, Icon: Microphone, onClick: () => setVoiceOpen(true), bg: "#FF3B30", fg: "#FFFFFF" },
    { key: "recurring", title: "Recurring", show: isManager, Icon: ArrowsClockwise, onClick: () => setRecurringOpen(true), bg: "#E0F2FE", fg: "#075985" },
    { key: "team", title: "Team", show: can("employee_directory"), Icon: Users, onClick: () => setTeamOpen(true), bg: "#F3E8FF", fg: "#6B21A8" },
    { key: "access", title: "Access", show: can("user_management"), Icon: ShieldCheck, onClick: () => setAccessOpen(true), bg: "#FEF3C7", fg: "#92400E" },
    { key: "meetings", title: "Meetings", show: can("meetings"), Icon: VideoCamera, onClick: () => setMeetingsOpen(true), bg: "#FCE7F3", fg: "#9D174D" },
    { key: "schedule", title: "Schedule", show: can("schedule_view"), Icon: CalendarBlank, onClick: () => setScheduleOpen(true), bg: "#CCFBF1", fg: "#0F766E" },
    { key: "timeoff", title: "Time Off", show: can("timeoff_request"), Icon: CalendarX, onClick: () => setTimeoffOpen(true), bg: "#FEE2E2", fg: "#991B1B" },
    { key: "availability", title: "Availability", show: can("availability_manage"), Icon: CalendarCheck, onClick: () => setAvailabilityOpen(true), bg: "#E0E7FF", fg: "#3730A3" },
    { key: "comm", title: isManager ? "Team Messages" : "Message Management", show: allowCommNotes, Icon: ChatCircleText, onClick: () => setCommOpen(true), bg: "#DBEAFE", fg: "#1E40AF", badge: commUnread },
    { key: "calendar", title: "My Calendar", show: can("calendar"), Icon: CalendarBlank, onClick: () => setCalendarOpen(true), bg: "#EEF2FF", fg: "#4338CA" },
    { key: "ai", title: "AI Assistant", show: can("ai_assistant"), Icon: Sparkle, onClick: () => setAiOpen(true), bg: "#DCFCE7", fg: "#166534" },
    { key: "guide", title: "App Guide", show: true, Icon: BookOpen, onClick: () => setGuideOpen(true), bg: "#DCFCE7", fg: "#166534" },
    { key: "settings", title: "Settings", show: true, Icon: GearSix, onClick: () => setSettingsOpen(true), bg: "#E2E8F0", fg: "#334155" },
  ];
  const availableToolbar = toolbarItems.filter((b) => b.show);
  const visibleToolbar = applyLayout(availableToolbar, prefs?.toolbar_layout);

  // Customizable dashboard widgets
  const collapsedWidgets = prefs?.collapsed_widgets || [];
  const isCollapsed = (k) => collapsedWidgets.includes(k);
  const toggleCollapse = (k) => {
    const next = collapsedWidgets.includes(k) ? collapsedWidgets.filter((x) => x !== k) : [...collapsedWidgets, k];
    savePrefs({ collapsed_widgets: next }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const dashWidgets = [
    { key: "reminders", title: "Reminders", show: true, render: () => <RemindersWidget key={remindersKey} onOpenTask={openTaskById} collapsed={isCollapsed("reminders")} onToggleCollapse={() => toggleCollapse("reminders")} /> },
    { key: "messages", title: "Team Messages", show: allowCommNotes, render: () => <CommWidget refreshKey={commKey} onOpen={() => setCommOpen(true)} collapsed={isCollapsed("messages")} onToggleCollapse={() => toggleCollapse("messages")} /> },
    { key: "recurring", title: "Recurring Tasks", show: isManager, render: () => <RecurringDashboardWidget refreshKey={recurringKey} onManage={() => setRecurringOpen(true)} onOpenTask={openTaskById} collapsed={isCollapsed("recurring")} onToggleCollapse={() => toggleCollapse("recurring")} /> },
    { key: "stats", title: "Stats Overview", show: isManager, render: () => (
      <div className="rounded-2xl border-2 border-black bg-card hard-shadow-sm p-4">
        <div className={`flex items-center justify-between ${isCollapsed("stats") ? "" : "mb-3"}`}>
          <div className="flex items-center gap-2"><ListChecks size={20} weight="bold" /><h3 className="font-display font-bold">Stats Overview</h3></div>
          <button data-testid="collapse-stats" onClick={() => toggleCollapse("stats")} title={isCollapsed("stats") ? "Expand" : "Collapse"} className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center shrink-0">
            {isCollapsed("stats") ? <CaretDown size={15} weight="bold" /> : <CaretUp size={15} weight="bold" />}
          </button>
        </div>
        {!isCollapsed("stats") && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STAT_META.map((s, i) => (
            <motion.button
              key={s.key} data-testid={`stat-${s.key}`} onClick={() => setTab(s.key)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`press text-left rounded-2xl border border-border bg-card p-5 ${tab === s.key ? "ring-2 ring-primary" : ""}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.bg }}><s.Icon size={22} weight="bold" /></div>
              <p className="font-display text-4xl font-bold mt-3 leading-none">{dash?.counts?.[s.key] ?? "–"}</p>
              <p className="text-xs font-bold uppercase tracking-[0.14em] mt-1.5 text-muted-foreground">{s.label}</p>
            </motion.button>
          ))}
        </div>
        )}
      </div>
    ) },
  ];
  const availableWidgets = dashWidgets.filter((w) => w.show);
  const visibleWidgets = applyLayout(availableWidgets, prefs?.dashboard_layout);

  const saveLayout = (kind, layout) => {
    const field = kind === "toolbar" ? { toolbar_layout: layout } : kind === "nav" ? { nav_layout: layout } : { dashboard_layout: layout };
    savePrefs(field)
      .then(() => toast.success("Layout saved"))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const resetLayout = (kind) => saveLayout(kind, { order: [], hidden: [] });

  const commands = [
    ...navGroups.flatMap((g) => g.items.filter((i) => i.show).map((i) => ({ label: i.label, Icon: i.Icon, run: i.onClick, group: g.group }))),
    ...(canCreateTask ? [
      { label: "New Task", Icon: Plus, run: openNew, group: "Actions" },
    ] : []),
    ...(isManager ? [
      { label: "Roll over overdue", Icon: ArrowClockwise, run: rollover, group: "Actions" },
    ] : []),
    { label: "Customize toolbar", Icon: Faders, run: () => setCustomizerType("toolbar"), group: "Actions" },
    { label: "Customize dashboard", Icon: Faders, run: () => setCustomizerType("dashboard"), group: "Actions" },
  ];

  const NavBtn = ({ item, compact }) => (
    <button data-testid={`nav-${item.key}`} onClick={item.onClick} title={compact ? item.label : undefined}
      className={`relative w-full flex items-center gap-3 h-10 rounded-xl text-sm font-semibold transition-colors ${compact ? "justify-center px-0" : "px-3"} ${activeKey === item.key ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"}`}>
      {activeKey === item.key && !compact && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-primary" />}
      <item.Icon size={20} weight={activeKey === item.key ? "fill" : "regular"} />
      {!compact && item.label}
      {item.badge > 0 && <span data-testid={`nav-${item.key}-badge`} className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold border-2 border-black flex items-center justify-center">{item.badge > 9 ? "9+" : item.badge}</span>}
    </button>
  );

  const SidebarNav = () => (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
      <div className="flex items-center justify-between px-3 -mb-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Menu</p>
        <button data-testid="customize-nav-btn" onClick={() => { setMobileMenuOpen(false); setCustomizerType("nav"); }}
          className="press flex items-center gap-1 h-7 px-2.5 rounded-full border border-border bg-muted/60 text-[11px] font-bold text-foreground/70">
          <Faders size={12} weight="bold" /> Edit order
        </button>
      </div>
      {navGroups.map((g) => {
        const items = applyLayout(g.items.filter((i) => i.show), prefs?.nav_layout);
        if (!items.length) return null;
        return (
          <div key={g.group}>
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{g.group}</p>
            <div className="space-y-1">{items.map((i) => <NavBtn key={i.key} item={i} />)}</div>
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header — quick-access icon bar */}
      <header className="sticky top-0 z-40 bg-card/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <img src={logo} alt="Short Stack Hawai'i" className="h-9 md:h-10 w-auto" data-testid="app-logo" />
            <div className="hidden sm:block">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{user.role?.replace(/_/g, " ")}</p>
            </div>
          </div>
          <div className="flex-1 max-w-md relative">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setTab("all"); loadAll(); } }}
              placeholder="Search tasks & notes… (⌘K)"
              className="w-full h-10 pl-11 pr-3 rounded-full border border-border bg-muted/60 outline-none focus:ring-2 focus:ring-primary focus:bg-card text-sm transition-colors"
            />
          </div>
          {/* Desktop icon bar */}
          <div className="hidden md:flex items-center gap-1.5 ml-auto">
            {visibleToolbar.map((b) => (
              <button key={b.key} data-testid={`${b.key}-open-btn`} onClick={b.onClick} title={b.title}
                style={{ backgroundColor: b.bg, color: b.fg, borderColor: "transparent" }}
                className={`press relative w-9 h-9 rounded-full border flex items-center justify-center ${activeKey === b.key ? "ring-2 ring-primary ring-offset-2 ring-offset-card" : ""}`}>
                <b.Icon size={16} weight="bold" />
                {b.badge > 0 && (
                  <span data-testid={`${b.key}-unread-badge`} className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold border-2 border-black flex items-center justify-center">{b.badge > 9 ? "9+" : b.badge}</span>
                )}
              </button>
            ))}
            {/* Customize layout */}
            <div className="relative">
              <button data-testid="customize-btn" onClick={() => setCustMenuOpen((o) => !o)} title="Customize layout"
                className={`press w-9 h-9 rounded-full border flex items-center justify-center bg-muted/60 text-foreground/70 ${custMenuOpen ? "ring-2 ring-primary" : ""}`}>
                <Faders size={16} weight="bold" />
              </button>
              {custMenuOpen && (
                <>
                  <div className="fixed inset-0 z-[45]" onClick={() => setCustMenuOpen(false)} />
                  <div data-testid="customize-menu" className="absolute right-0 mt-2 w-52 bg-card border border-border rounded-xl hard-shadow p-1.5 z-[46]">
                    <p className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Customize layout</p>
                    <button data-testid="customize-toolbar-btn" onClick={() => { setCustMenuOpen(false); setCustomizerType("toolbar"); }} className="w-full flex items-center gap-2.5 px-2.5 h-10 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted transition-colors"><Faders size={17} weight="bold" /> Toolbar icons</button>
                    <button data-testid="customize-dashboard-btn" onClick={() => { setCustMenuOpen(false); setCustomizerType("dashboard"); }} className="w-full flex items-center gap-2.5 px-2.5 h-10 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted transition-colors"><House size={17} weight="bold" /> Dashboard widgets</button>
                  </div>
                </>
              )}
            </div>
            <NotificationBell />
            <div className="w-9 h-9 rounded-full bg-secondary text-white flex items-center justify-center font-bold text-sm">{user.name?.[0]}</div>
            <button data-testid="logout-btn" onClick={logout} className="press w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center hover:bg-muted"><SignOut size={16} weight="bold" /></button>
          </div>
          {/* Mobile: guide + notifications + menu */}
          <div className="md:hidden flex items-center gap-2 ml-auto">
            <button data-testid="mobile-guide-btn" title="App Guide" onClick={() => setGuideOpen(true)} className="press w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center"><BookOpen size={18} weight="bold" /></button>
            <NotificationBell />
            <button data-testid="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} className="w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center"><List size={18} weight="bold" /></button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div>
        <main className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-28 md:pb-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{ROLE_SUBTITLE[user.role] || "Welcome back"}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">{user.name?.split(" ")[0]}'s Day</h2>
            </div>
            {isManager && (
              <button data-testid="rollover-btn" onClick={rollover} className="press hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full bg-card font-semibold text-sm border border-border">
                <ArrowClockwise size={16} weight="bold" /> Roll over overdue
              </button>
            )}
          </div>

          {/* Customizable dashboard widgets */}
          <div className="space-y-6 mb-8">
            {visibleWidgets.map((w) => (
              <div key={w.key} data-testid={`widget-${w.key}`}>{w.render()}</div>
            ))}
          </div>

          {!isManager && <h3 className="font-display text-xl font-bold mb-3 mt-2">My tasks</h3>}

          {/* Tabs + category filter */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 mb-2">
            {TABS.filter((t) => !t.managerOnly || isManager).map((t) => (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`press shrink-0 h-9 px-4 rounded-full text-sm font-semibold border transition-colors flex items-center gap-1.5 ${tab === t.key ? "bg-secondary text-white border-secondary" : "bg-card border-border"}`}>
                {t.label}
                {t.key === "review" && dash?.counts?.to_review > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">{dash.counts.to_review}</span>
                )}
              </button>
            ))}
          </div>
          {tab === "all" && (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
              <button onClick={() => setCategory("all")} className={`press shrink-0 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${category === "all" ? "bg-primary text-white border-primary" : "bg-card border-border"}`}>All</button>
              {CATEGORIES.map((c) => (
                <button key={c} data-testid={`filter-cat-${c}`} onClick={() => setCategory(c)} className={`press shrink-0 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${category === c ? "bg-primary text-white border-primary" : "bg-card border-border"}`}>{c}</button>
              ))}
            </div>
          )}

          {/* Task list */}
          {tasks.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-12 text-center bg-card/50">
              <CalendarBlank size={40} className="mx-auto text-muted-foreground" />
              <p className="font-display text-xl font-bold mt-3">Nothing here</p>
              <p className="text-sm text-muted-foreground">No tasks in this view.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {tasks.map((t, i) => <TaskCard key={t.id} task={t} index={i} onClick={setDetail} />)}
            </div>
          )}
        </main>
      </div>

      {/* Desktop floating add */}
      {canCreateTask && (
        <button data-testid="add-task-fab" onClick={openNew} className="press hidden md:flex fixed bottom-8 right-8 h-14 px-6 rounded-full bg-[#FF3B30] text-white font-semibold hard-shadow items-center gap-2 z-40">
          <Plus size={20} weight="bold" /> New Task
        </button>
      )}

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-card border-t border-border z-40 flex items-center justify-around px-2">
        <button data-testid="nav-home" onClick={goHome} className="flex flex-col items-center gap-0.5 text-foreground/70"><House size={22} weight="bold" /><span className="text-[10px] font-semibold">Home</span></button>
        {can("schedule_view") && <button data-testid="nav-schedule-m" onClick={() => setScheduleOpen(true)} className="flex flex-col items-center gap-0.5 text-foreground/70"><CalendarBlank size={22} weight="bold" /><span className="text-[10px] font-semibold">Schedule</span></button>}
        {voiceShow ? (
          <button data-testid="nav-voice" onClick={() => setVoiceOpen(true)} className="press -mt-6 w-14 h-14 rounded-full bg-[#FF3B30] text-white hard-shadow flex items-center justify-center"><Microphone size={26} weight="fill" /></button>
        ) : canCreateTask && (
          <button data-testid="nav-add" onClick={openNew} className="press -mt-6 w-14 h-14 rounded-full bg-[#FF3B30] text-white hard-shadow flex items-center justify-center"><Plus size={26} weight="bold" /></button>
        )}
        {canCreateTask && voiceShow && <button data-testid="nav-add-m" onClick={openNew} className="flex flex-col items-center gap-0.5 text-foreground/70"><Plus size={22} weight="bold" /><span className="text-[10px] font-semibold">New Task</span></button>}
        {can("ai_assistant") && <button data-testid="nav-ai" onClick={() => setAiOpen(true)} className="flex flex-col items-center gap-0.5 text-foreground/70"><Sparkle size={22} weight="bold" /><span className="text-[10px] font-semibold">AI</span></button>}
        <button data-testid="nav-menu" onClick={() => setMobileMenuOpen(true)} className="flex flex-col items-center gap-0.5 text-foreground/70"><List size={22} weight="bold" /><span className="text-[10px] font-semibold">Menu</span></button>
      </nav>

      {/* Mobile nav drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div data-testid="mobile-menu" onClick={(e) => e.stopPropagation()} className="relative bg-card w-72 max-w-[80%] h-full flex flex-col border-r border-border animate-in slide-in-from-left duration-200">
            <div className="h-16 flex items-center justify-between px-5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <img src={logo} alt="Short Stack Hawai'i" className="h-9 w-auto" />
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"><X size={16} weight="bold" /></button>
            </div>
            <SidebarNav />
            <div className="p-3 border-t border-border flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-secondary text-white flex items-center justify-center font-bold text-sm">{user.name?.[0]}</div>
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{user.role?.replace(/_/g, " ")}</p></div>
              <button data-testid="logout-btn-m" onClick={logout} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"><SignOut size={16} weight="bold" /></button>
            </div>
          </div>
        </div>
      )}

      <TaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={() => { refreshAll(); setRemindersKey((k) => k + 1); }} users={users} editing={editing} />
      {detail && <TaskDetail task={detail} onClose={() => setDetail(null)} onChanged={onDetailChanged} onEdit={openEdit} />}
      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} onTasksCreated={refreshAll} />
      <TeamPanel open={teamOpen} onClose={() => setTeamOpen(false)} onChanged={loadUsers} />
      <VoiceCapture open={voiceOpen} onClose={() => setVoiceOpen(false)} onCreated={() => { refreshAll(); loadDash(); setRemindersKey((k) => k + 1); setRecurringKey((k) => k + 1); }} />
      <RecurringPanel open={recurringOpen} onClose={() => setRecurringOpen(false)} onChanged={() => { refreshAll(); loadDash(); setRecurringKey((k) => k + 1); }} />
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} onCustomizeToolbar={() => { setSettingsOpen(false); setCustomizerType("toolbar"); }} onCustomizeDashboard={() => { setSettingsOpen(false); setCustomizerType("dashboard"); }} />
      <AppGuide open={guideOpen} onClose={() => setGuideOpen(false)} isManager={isManager} isOwner={user.role === "owner"} />
      <MeetingsPanel open={meetingsOpen} onClose={() => setMeetingsOpen(false)} onChanged={() => {}} onOpenSettings={() => setSettingsOpen(true)} onGenerate={(mtg) => setGenerateMeeting(mtg)} />
      <SchedulePanel open={scheduleOpen} onClose={() => setScheduleOpen(false)} onChanged={() => {}} />
      <TimeOffPanel open={timeoffOpen} onClose={() => setTimeoffOpen(false)} onChanged={() => {}} />
      <AvailabilityPanel open={availabilityOpen} onClose={() => setAvailabilityOpen(false)} />
      <CommNotesPanel open={commOpen} onClose={() => setCommOpen(false)} />
      <CalendarPanel open={calendarOpen} onClose={() => setCalendarOpen(false)} />
      <LayoutCustomizer
        open={customizerType === "toolbar"}
        onClose={() => setCustomizerType(null)}
        title="Customize toolbar"
        subtitle="Show, hide and reorder your toolbar icons."
        items={availableToolbar.map((b) => ({ key: b.key, title: b.title }))}
        layout={prefs?.toolbar_layout}
        onSave={(l) => saveLayout("toolbar", l)}
        onReset={() => resetLayout("toolbar")}
      />
      <LayoutCustomizer
        open={customizerType === "dashboard"}
        onClose={() => setCustomizerType(null)}
        testid="dashboard-customizer"
        title="Customize dashboard"
        subtitle="Show, hide and reorder your dashboard widgets."
        items={availableWidgets.map((w) => ({ key: w.key, title: w.title }))}
        layout={prefs?.dashboard_layout}
        onSave={(l) => saveLayout("dashboard", l)}
        onReset={() => resetLayout("dashboard")}
      />
      <LayoutCustomizer
        open={customizerType === "nav"}
        onClose={() => setCustomizerType(null)}
        testid="nav-customizer"
        title="Customize menu"
        subtitle="Show, hide and reorder the links in your menu."
        items={availableNav.map((i) => ({ key: i.key, title: i.label }))}
        layout={prefs?.nav_layout}
        onSave={(l) => saveLayout("nav", l)}
        onReset={() => resetLayout("nav")}
      />
      {generateMeeting && <MeetingSummaryFlow meeting={generateMeeting} onClose={() => setGenerateMeeting(null)} onDone={() => { setGenerateMeeting(null); }} />}
      <EmployeesAccess open={accessOpen} onClose={() => setAccessOpen(false)} />
      {paletteOpen && <CommandPalette commands={commands} users={users} onTask={openTaskById} onEmployee={() => setTeamOpen(true)} onClose={() => setPaletteOpen(false)} />}
      {showFirstTime && <FirstTimeSetup onDone={() => loadPrefs()} />}
    </div>
  );
}

function CommandPalette({ commands, users, onTask, onEmployee, onClose }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [tasks, setTasks] = useState([]);

  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setTasks([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/tasks", { params: { search: query } }); setTasks(data.slice(0, 6)); }
      catch { setTasks([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const staticMatches = commands
    .filter((c) => c.label.toLowerCase().includes(q.toLowerCase()))
    .map((c) => ({ ...c, type: "command" }));
  const taskMatches = tasks.map((t) => ({
    label: t.title, sub: (t.status || "").replace(/_/g, " "), Icon: ListChecks, group: "Task", type: "task",
    run: () => { onClose(); onTask(t.id); },
  }));
  const ql = q.trim().toLowerCase();
  const userMatches = (ql.length >= 2 ? users.filter((u) =>
    (u.name || "").toLowerCase().includes(ql) || (u.email || "").toLowerCase().includes(ql) || (u.role || "").toLowerCase().includes(ql)
  ).slice(0, 5) : []).map((u) => ({
    label: u.name, sub: (u.role || "").replace(/_/g, " "), Icon: Users, group: "Team", type: "employee",
    run: () => { onClose(); onEmployee(); },
  }));
  const all = [...staticMatches, ...taskMatches, ...userMatches];

  const run = (c) => c.run();
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, all.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (all[idx]) run(all[idx]); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[14vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, y: -12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.15 }}
        data-testid="command-palette" onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-card rounded-2xl border border-border hard-shadow overflow-hidden">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
          <MagnifyingGlass size={18} className="text-muted-foreground" />
          <input autoFocus data-testid="command-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search pages, actions, tasks & people…" className="flex-1 bg-transparent outline-none text-sm" />
          <kbd className="font-mono text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-2">
          {all.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">{q.trim().length < 2 ? "Type to search pages, tasks & people…" : "No results"}</p>}
          {all.map((c, i) => (
            <button key={`${c.type}-${c.label}-${i}`} data-testid={`command-${c.type}-${i}`} onMouseEnter={() => setIdx(i)} onClick={() => run(c)}
              className={`w-full flex items-center gap-3 px-4 h-11 text-sm text-left transition-colors ${i === idx ? "bg-primary/10 text-primary" : "text-foreground/80"}`}>
              <c.Icon size={18} weight={i === idx ? "fill" : "regular"} />
              <span className="truncate">{c.label}</span>
              <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-muted-foreground shrink-0">{c.sub || c.group}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
